/*
 * SchedulerPicker
 * Suggests a starting social media scheduler for a small law firm from five answers.
 * Uses only the post's own comparison table and "Bottom line". No prices, scores or invented numbers.
 *
 * Rules (first match wins)
 *   1. A partner must approve posts first        -> Planable  (multi-level approvals)
 *   2. Partners need deep team reporting         -> Hootsuite (only if you truly need its reporting depth)
 *   3. Partners need detailed analytics          -> Metricool (detailed analytics and competitor tracking)
 *   4. Wants old posts recycled automatically    -> Zoho Social (evergreen recycling)
 *   5. Drafts posts with ChatGPT or Claude       -> PostEverywhere (works with ChatGPT and Claude)
 *   6. Otherwise                                 -> Buffer (simple queue, start here if one person posts)
 *   "Also worth a look": every other tool whose rule also matched, in the same order.
 *   The result appears once all five questions are answered.
 * No tracking, storage or network calls.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

import { pick, type Tool } from "./scheduler-picker.logic";

const WHY: Record<Tool, string> = {
  Planable: "Built for firms with approval layers: visual mockups and multi-level approvals, so nothing goes live before a partner signs off.",
  Hootsuite: "Built for mid-sized firms and agencies: deep analytics, team tools and bulk scheduling. Pick it only if you truly need that reporting depth.",
  Metricool: "Built for firms that report to partners: detailed analytics and competitor tracking.",
  "Zoho Social": "Built for firms that want automation: SmartQ timing, RSS automation and evergreen recycling.",
  PostEverywhere: "Built for firms using AI assistants: official API connections and works with ChatGPT and Claude.",
  Buffer: "Built for solo attorneys and tiny firms: a simple queue across many platforms and a built-in AI assistant.",
};

type Q = { id: string; label: string; options: { value: string; text: string }[] };

const QUESTIONS: Q[] = [
  { id: "who", label: "Who posts for the firm?", options: [{ value: "one", text: "One person" }, { value: "team", text: "A small team" }] },
  { id: "approve", label: "Must a partner approve posts before they go live?", options: [{ value: "yes", text: "Yes" }, { value: "no", text: "No" }] },
  {
    id: "report",
    label: "How much reporting do partners expect?",
    options: [
      { value: "basic", text: "Basic is fine" },
      { value: "detailed", text: "Detailed analytics" },
      { value: "deep", text: "Deep team reporting" },
    ],
  },
  { id: "recycle", label: "Do you want your best posts recycled automatically?", options: [{ value: "yes", text: "Yes" }, { value: "no", text: "No" }] },
  { id: "ai", label: "Do you draft posts with ChatGPT or Claude?", options: [{ value: "yes", text: "Yes" }, { value: "no", text: "No" }] },
];



export default function SchedulerPicker() {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const done = QUESTIONS.every((q) => answers[q.id]);
  const result = done ? pick(answers) : [];
  const [main, ...also] = result;

  const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-lg border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Which scheduler should your firm start with?</h3>
      <p className="mt-1 text-sm text-muted-foreground">Answer five questions. Nothing is saved or sent anywhere.</p>

      <div className="mt-5 grid gap-5">
        {QUESTIONS.map((q) => (
          <fieldset key={q.id}>
            <legend className="text-sm font-medium">{q.label}</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {q.options.map((o) => {
                const on = answers[q.id] === o.value;
                return (
                  <Button
                    key={o.value}
                    type="button"
                    variant={on ? "default" : "outline"}
                    aria-pressed={on}
                    onClick={() => setAnswers((s) => ({ ...s, [q.id]: o.value }))}
                    className={`rounded-md border px-3 py-2 text-sm ${focusRing} ${
                      on ? "border-primary bg-primary text-primary-foreground" : "border-input bg-card"
                    }`}
                  >
                    {o.text}
                  </Button>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4 text-sm">
        {!done ? (
          <p className="text-muted-foreground">Answer all five questions to see a starting point.</p>
        ) : (
          <div>
            <p className="font-semibold">Start with {main}.</p>
            <p className="mt-2">{main ? WHY[main] : null}</p>
            {main === "Buffer" && answers.who === "team" && (
              <p className="mt-2 text-muted-foreground">If your team grows or a partner starts signing off on posts, look at Planable.</p>
            )}
            {also.length > 0 && <p className="mt-2">Also worth a look: {also.join(", ")}.</p>}
            {answers.ai === "yes" && (
              <p className="mt-2 text-muted-foreground">Whatever you choose, an attorney should review every AI draft before it is scheduled.</p>
            )}
            <p className="mt-2 text-muted-foreground">
              Before you buy, check it supports every channel you use, including Google Business Profile, and confirm current prices on the vendor's site.
            </p>
            <p className="mt-3">
              <Link to="/services/social-media-marketing-for-law-firms" className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Rather hand it off? See our social media service for law firms →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
